import React from 'react';
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Image,
  Center,
  Divider,
  Text,
  Link,
  Button,
  Icon,
} from '@chakra-ui/react';
import ProjectItem from '../projectItem';
import '../styles/spec-list.css';
import { RiComputerLine } from 'react-icons/ri';
import { FiExternalLink } from 'react-icons/fi';

/**
 * Card + modal for a live personal web app. The three My Projects entries share
 * the same shape — overview, screenshot, tools, link — so they share one
 * component and differ only by the data in src/data/web-projects.js.
 */
export default function WebProjectCard({ project }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  // A missing screenshot drops the whole PREVIEW block rather than leaving a
  // broken-image frame in the middle of the modal
  const [hasScreenshot, setHasScreenshot] = React.useState(true);
  const {
    title,
    subtitle,
    tags,
    url,
    paragraphs,
    screenshot,
    caption,
    toolGroups,
  } = project;

  return (
    <div>
      <div className="card-container" onClick={onOpen}>
        <ProjectItem title={title} subtitle={subtitle} tags={tags} />
      </div>

      <Modal
        onClose={onClose}
        size={{ base: 'full', lg: '4xl' }}
        isOpen={isOpen}
        scrollBehavior={'inside'}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={4}>
            {title}
            <Box className="eyebrow" mt={1}>
              {subtitle}
            </Box>
            <Divider mt={4} />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box className="eyebrow" mb={3}>
              OVERVIEW
            </Box>
            {paragraphs.map((paragraph, index) => (
              <Box key={paragraph.slice(0, 24)} mt={index === 0 ? 0 : 6}>
                <Text className="modal-copy">{paragraph}</Text>
              </Box>
            ))}

            {hasScreenshot && (
              <>
                <Box className="eyebrow" mt={12} mb={4}>
                  PREVIEW
                </Box>
                <figure className="modal-figure">
                  <Image
                    className="modal-figure__media"
                    src={screenshot}
                    alt={`${title} — ${caption}`}
                    onError={() => setHasScreenshot(false)}
                  />
                  <figcaption className="modal-figure__caption">
                    {caption}
                  </figcaption>
                </figure>
              </>
            )}

            <Box className="eyebrow" mt={12} mb={6}>
              TOOLS
            </Box>
            <div className="spec-grid">
              {toolGroups.map(group => (
                <div className="spec-group" key={group.title}>
                  <div className="spec-group__title">{group.title}</div>
                  <dl className="spec-list">
                    {group.items.map(([label, value]) => (
                      <div className="spec-row" key={label}>
                        <dt className="spec-row__label">{label}</dt>
                        <dd className="spec-row__value">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>

            <Box className="eyebrow" mt={12} mb={3}>
              LIVE SITE
            </Box>
            <Box mt={3}>
              <Link href={url} isExternal>
                <Button
                  colorScheme="brand"
                  variant="outline"
                  rightIcon={<Icon as={FiExternalLink} />}
                >
                  {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </Button>
              </Link>
            </Box>

            <Center mt={12}>
              <Divider mr={5} />
              <RiComputerLine size={50} />
              <Divider ml={5} />
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
